
import { LineItemStatusSummary } from "@/models/OrderLineItemSummary";
import ExpandTransition from "@/components/ExpandTransition";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { SEARCH_VENDORS, VENDOR_BY_ID } from "@/gql/vendors";
import DateFormatting from "@/mixins/DateFormatting";

type PoSize = {
	name: string;
	ordered: number;
	received: number;
	backOrdered: number;
};

type PoLineItem = {
	lineItemId: number;
	lineItemNumber: number;
	systemCategoryId: number;
	styleCode: string;
	description: string;
	ordered: number;
	received: number;
	backOrdered: number;
	sizes: PoSize[];
	hasSizes: boolean;
};

type PurchaseOrder = {
	purchaseOrderId: number;
	purchaseOrderNumber: string;
	vendorId: number;
	vendor: string;
	ordered: number;
	received: number;
	orderType: string;
	backOrdered: number;
	salesOrderId: number | null;
	purchaseOrderDate: string | null;
	orderStatus: string;
	purchaseOrderPrinted: string | null;
	packingSlipPrinted: string | null;
};

type PoDetailed = {
	purchaseOrder: PurchaseOrder;
	lineItems: PoLineItem[];
};

type PoResponse = {
	data: PoDetailed[];
};

type Address = {
	vendorAddressId: number;
	label: string;
	addressLine1: string;
	addressLine2: string;
	addressTypeId: number | null;
	city: string;
	state: string;
	postalCode: string;
	isPrimaryAddress: boolean;
};

type Contact = {
	vendorContactId: number;
	primaryContact: boolean;
	firstName: string;
	lastName: string;
	workPhoneNumber: string | null;
	cellPhoneNumber: string;
	email: string;
	isActive: boolean;
};

type Vendor = {
	vendorId: number;
	companyName: string;
	addresses: Address[];
	contacts: Contact[];
	vendorContactId: number | null;
};

export default {
	name: "ProcessingPurchaseOrdersStatus",
	components: {
		ExpandTransition,
		SystemCategoryIcon,
	},
	mixins: [DateFormatting],
	props: {
		salesOrderId: {
			type: Number,
			required: true,
		},
		lineItems: {
			type: Array as () => LineItemStatusSummary[],
			required: false,
		},
		posToUpdate: {
			type: Array as () => PoDetailed[],
			required: false,
		},
		orphanedPoIds: {
			type: Set,
			required: false,
			default: () => new Set(),
		},
		hardRefresh: {
			type: Number,
			required: false,
		},
		canPrintVendorPOs: {
			type: Boolean,
			default: false,
		},
	},
	data(): {
		showDateByRow: number | null;
		showItemTableByRow: string | null;
		showSubTableByRow: string | null;
		showTable: string[];
		processingOrders: PoDetailed[];
		editVendor: PurchaseOrder | null;
		editPoDate: PurchaseOrder | null;
		vendorSearch: Vendor | null;
		vendorResults: Vendor[];
		contacts: Contact[];
		addresses: Address[];
		showOrderDate: boolean;
		showOnlyOnOrder: boolean;
		showCancelled: boolean;
	} {
		return {
			showDateByRow: null,
			showItemTableByRow: null,
			showSubTableByRow: null,
			showTable: [""],
			processingOrders: [],
			editVendor: null,
			editPoDate: null,
			vendorSearch: null,
			vendorResults: [],
			contacts: [],
			addresses: [],
			showOrderDate: false,
			showOnlyOnOrder: false,
			showCancelled: false,
		};
	},
	methods: {
		async getProcessingPurchaseOrders(salesOrderId: number): Promise<PoResponse> {
			try {
				const response = await this.$http.get(`/api/orders/${salesOrderId}/purchase-orders`);
				if (response.data && Array.isArray(response.data.data)) {
					const filteredData = response.data.data.filter(
						(po: any) => po.purchaseOrder.dropShip === false
					);
					return { data: filteredData };
				} else {
					console.warn("Received data may not be an array", response.data);
					return { data: [] };
				}
			} catch (error) {
				console.error("Error fetching dropship orders:", error);
				return { data: [] };
			}
		},
		poLineNumberMismatch(poNumber: string, lineItems: PoLineItem[]): number | null {
			if (!poNumber || !lineItems?.length) return null;
			const parts = poNumber.split('-');
			if (parts.length < 2) return null;
			const embeddedLineNum = parseInt(parts[1]);
			if (isNaN(embeddedLineNum)) return null;
			const currentLineNum = lineItems[0]?.lineItemNumber;
			if (!currentLineNum || embeddedLineNum === currentLineNum) return null;
			return currentLineNum;
		},
		renderOrderStatus(orderStatus: string) {
			let status = {
				label: "",
				color: "",
			};
			switch (orderStatus) {
				case "OnOrder":
					status.label = "On Order";
					status.color = "table-status-gray";
					break;
				case "PartiallyReceived":
					status.label = "Partially Received";
					status.color = "table-status-gray";
					break;
				case "Received":
					status.label = "Received";
					status.color = "table-status-green";
					break;
				case "Cancelled":
					status.label = "Cancelled";
					status.color = "table-status-red";
					break;
				default:
					status.label = "Error";
					status.color = "table-status-orange";
			}

			return status;
		},
		async searchVendors(input: any) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: SEARCH_VENDORS,
					variables: {
						searchTerm: `${input}`,
					},
				});
				this.vendorResults = res.data.response.items;
			} catch (error) {
				console.log(error);
			}
		},
		async getAndSetVendorById(vendorId: number) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: VENDOR_BY_ID,
					variables: {
						id: vendorId,
					},
				});

				this.vendorSearch = res.data.response;

				//set the assigned vendor data
				this.setVendor();
			} catch (error) {
				console.log(error);
			}
		},
		setVendor() {
			console.log(this.vendorSearch);

			if (this.vendorSearch) {
				//assign the selected vendor contacts and addresses
				this.contacts = this.vendorSearch.contacts || [];
				this.addresses = this.vendorSearch.addresses || [];

				//assign the selected vendor data to poData
				if (this.contacts.some((obj) => obj.primaryContact == true)) {
					//assign the first found primary contact
					this.vendorSearch.vendorContactId =
						this.contacts.find((obj) => obj.primaryContact == true)?.vendorContactId ?? null;
				} else {
					//assign a default first contact
					this.vendorSearch.vendorContactId = this.contacts[0]?.vendorContactId;
				}
			}
		},
		async saveVendor(po: PurchaseOrder) {
			try {
				const res = await this.$http.post(
					`/api/purchasing/orders/${po.purchaseOrderId}/edit-vendor`,
					{
						vendorId: this.vendorSearch?.vendorId,
						vendorContactId: this.vendorSearch?.vendorContactId,
					}
				);
				console.log(res);
				this.init();
			} catch (error) {
				console.log(error);
			}
		},
		async saveOrderDate(po: PurchaseOrder) {
			try {
				const res = await this.$http.post(
					`/api/purchasing/orders/${po.purchaseOrderId}/edit-date`,
					{
						orderDate: po.purchaseOrderDate,
					}
				);
				console.log(res);
				this.init();
			} catch (error) {
				console.log(error);
			}
		},
		async init() {
			const response = await this.getProcessingPurchaseOrders(this.salesOrderId);
			this.processingOrders = response.data;

			if (this.processingOrders.length > 0) {
				await this.getAndSetVendorById(this.processingOrders[0].purchaseOrder.vendorId);
			}
		},
	},
	watch: {
		hardRefresh() {
			this.init();
		},
	},
	computed: {
		hasCancelledOrders(): boolean {
			return this.processingOrders.some(
				(order: PoDetailed) => order.purchaseOrder.orderStatus === "Cancelled"
			);
		},
		displayedOrders() {
			let orders = this.processingOrders;
			if (this.showOnlyOnOrder) {
				return orders.filter(
					(order: PoDetailed) => order.purchaseOrder.orderStatus === "OnOrder"
				);
			}
			if (!this.showCancelled) {
				orders = orders.filter(
					(order: PoDetailed) => order.purchaseOrder.orderStatus !== "Cancelled"
				);
			}
			return orders;
		},
		overrideProcessingOrders() {
			return this.posToUpdate && this.posToUpdate.length > 0;
		},
	},
	async mounted() {
		await this.init();
	},
};
